import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../plans/plans.scss';
import CheckCircleIcon from '../../assets/icon-components/CheckCircle';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { fireCheckoutStart, firePlanChange, firePaymentTypeChange } from '../../utils/dataLayer';
import {
  getPlanFallbackAmount,
  renderCheckoutPlanPrice,
} from '../../utils/rhPromoPricing';
import { useRhReferralPricing } from '../../utils/useRhReferralPricing';

const PlanHeader = ({
  title,
  description,
  price,
  id,
  selectedPlan,
  onSelect
}: {
  title: string;
  description: string;
  price: React.ReactNode;
  id: string;
  selectedPlan: string;
  onSelect: (id: string) => void;
}) => {
  return (
    <div className='plan-card-header d-flex' >
      <div>
        <input
          type='radio'
          name='plan-type'
          className='plan-type-radio'
          checked={selectedPlan === id}
          onChange={() => onSelect(id)}
        />
        <span className='radio-btn'></span>
      </div>
      <div className='plan-card-header-info' >
        <div className='' >
          {title}
          <p>{description}</p>
        </div>
        <div className='plan-card-header-price' >{price}</div>
      </div>
    </div>
  );
};

const BloodTestPerYear = ({
  selectedTestCount,
  onTestCountChange,
  availableTestCounts
}: {
  selectedTestCount: number | null;
  onTestCountChange: (count: number) => void;
  availableTestCounts: number[];
}) => {
  return (
    <div className='blood-test-per-year' >
      <div className='text-center' >
        <h4>Select the amount of blood tests you want per year</h4>
        <p><CheckCircleIcon />Includes free nurse blood draw at home</p>
      </div>
      <div className='blood-test-per-year-buttons' >
        {availableTestCounts.map(count => (
          <button 
            key={count}
            onClick={(e) => {
              e.stopPropagation();
              onTestCountChange(count);
            }} 
            className={`btn ${selectedTestCount === count ? "active" : ""}`}
          >
            {count} test{count > 1 ? 's' : ''}
          </button>
        ))}
      </div>
    </div>
  );
};

const corePlan = {
  id: 'coreVitals',
  className: 'coreVitals-membership',
  title: 'CoreVitals Membership',
  description: 'Ongoing health tracking without imaging - up to 4x blood tests/year',
  price: <>~ £38<span>/month</span></>,
  showBloodTests: true,
  bannerImg: bannerImg1,
  list: [
    '1 blood biomarker test per year',
    'Includes free nurse blood draw at home',
    'Full biomarker panel',
    'Doctor review + consult',
    'Nutritionist guidance',
    'App access to track progress',
  ],
  nextSteps: [
    "Complete the onboarding form",
    "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
    "Review your detailed results in the CoreVitals app",
    "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations"
  ]
};

const OrderSummary = ({ 
  selectedPlanId, 
  paymentType,
  selectedTestCount,
  getPlanPrice
}: { 
  selectedPlanId: string;
  paymentType: PaymentType;
  selectedTestCount: number | null;
  getPlanPrice: (planId: PlanType) => React.ReactNode;
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  let itemsToShow = corePlan.list;
  
  // Build display title with number of tests
  let displayTitle = corePlan.title;
  if (selectedTestCount) {
    displayTitle = `${corePlan.title} ${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}`;
  }
  
  // Dynamically update the number of tests in the list
  if (selectedTestCount) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${selectedTestCount} blood biomarker test${selectedTestCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }

  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={corePlan.bannerImg} alt={`${displayTitle} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{displayTitle}</div>}
      {!isMobile && <p className='order-summary-description'>{corePlan.description}</p>}
      <ul className='order-summary-list'>
        {itemsToShow.map((item, index) => (
          <li key={`${item}-${index}`}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
      <div className='total-price'>
        <div>Total</div>
        <div>{getPlanPrice(selectedPlanId as PlanType)}</div>
      </div>
    </div>
  );
};

type PlanType = 'coreVitals' | '';
type PaymentType = 'monthly' | 'yearly';

interface SelectedPlanData {
  type: PlanType;
  testCount: number | null;
  product: StripeProduct | null;
  productId: string | null;
  paymentType: PaymentType;
}

const PlansCore = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('coreVitals');
  const [selectedTestCount, setSelectedTestCount] = useState<number | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType>('monthly');
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');
  useRhReferralPricing();

  // Get products by type
  const getCoreVitalsProducts = () => {
    return products
      .filter(p => p.key.startsWith('core_') && !p.key.startsWith('core_plus_'))
      .sort((a, b) => {
        const aNum = parseInt(a.key.match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.key.match(/\d+/)?.[0] || '0');
        return aNum - bNum;
      });
  };

  // Get available test counts for core plan
  const getAvailableTestCounts = (): number[] => {
    return getCoreVitalsProducts().map(p => parseInt(p.key.match(/\d+/)?.[0] || '0'));
  };

  // Read plan type from query parameter
  useEffect(() => {
    const planTypeParam = searchParams.get('plan_type') || searchParams.get('planType');
    if (planTypeParam === 'coreVitals' || planTypeParam === '') {
      setSelectedPlan(planTypeParam as PlanType);
    }
  }, [searchParams]);

  // Read payment type from query parameter
  useEffect(() => {
    const paymentTypeParam = searchParams.get('payment_type') || searchParams.get('paymentType');
    if (paymentTypeParam === 'yearly' || paymentTypeParam === 'monthly') {
      setPaymentType(paymentTypeParam);
    }
  }, [searchParams]);

  // Read blood test count from query parameter
  useEffect(() => {
    const testCountParam = searchParams.get('testCount') || searchParams.get('test_count');
    if (testCountParam) {
      const count = parseInt(testCountParam, 10);
      if (!isNaN(count) && count > 0) {
        setSelectedTestCount(count);
      }
    }
  }, [searchParams]);

  // Fire checkout start detection on mount
  useEffect(() => {
    fireCheckoutStart();
  }, []);

  // Fetch member details if token is present (after products are loaded)
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam || products.length === 0) return;

    const fetchMemberDetails = async () => {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}members?loading_strategy=with_subscription`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`,
            },
          }
        );

        const member = response.data;

        // Set test count from member data
        if (member.number_of_blood_test_plan) {
          const testCount = member.number_of_blood_test_plan;
          // Verify the test count is available in the products
          const availableCounts = getAvailableTestCounts();
          if (availableCounts.includes(testCount)) {
            setSelectedTestCount(testCount);
          }
        }

        // Set billing cycle from member data
        if (member.billing_cycle) {
          const billingCycle = member.billing_cycle.toLowerCase();
          if (billingCycle === 'yearly' || billingCycle === 'monthly') {
            setPaymentType(billingCycle as PaymentType);
          }
        }
      } catch (error) {
        console.error('Error fetching member details:', error);
      }
    };

    fetchMemberDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, products]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getStripeProducts();
        const activeProducts = response.products.filter((product: StripeProduct) => product.active);
        setProducts(activeProducts);
      } catch (err: any) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Auto-select first test count if not set from query param
  useEffect(() => {
    if (!selectedTestCount && products.length > 0) {
      const availableCounts = getAvailableTestCounts();
      if (availableCounts.length > 0) {
        setSelectedTestCount(availableCounts[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedTestCount]);

  // Get product for selected plan and test count
  const getSelectedProduct = (): StripeProduct | null => {
    if (!selectedTestCount) return null;
    
    const productKey = `core_${selectedTestCount}_test`;
    return products.find(p => p.key === productKey) || null;
  };

  // Get price for selected plan
  const getPlanPrice = (planId: PlanType): React.ReactNode => {
    // Get the product to use for pricing
    let productToUse: StripeProduct | null = null;
    
    if (selectedTestCount) {
      // Use selected product if test count is selected
      productToUse = getSelectedProduct();
    }
    
    // If no product found, use default (first available)
    if (!productToUse) {
      const availableProducts = getCoreVitalsProducts();
      productToUse = availableProducts[0] || null;
    }

    if (productToUse) {
      const amount = paymentType === 'monthly' 
        ? productToUse.monthly_amount 
        : productToUse.yearly_amount;
      
      if (amount !== null && amount !== undefined) {
        const showTilde = !selectedTestCount;
        return renderCheckoutPlanPrice({
          planId: 'coreVitals',
          amount,
          paymentType,
          showTilde,
        });
      }
    }
    
    return renderCheckoutPlanPrice({
      planId: 'coreVitals',
      amount: getPlanFallbackAmount('coreVitals') ?? 38,
      paymentType,
      showTilde: true,
    });
  };

  const handlePlanSelect = (planId: PlanType) => {
    setSelectedPlan(planId);
    // Auto-select first test count
    const availableCounts = getAvailableTestCounts();
    let testCountToUse = selectedTestCount;
    if (availableCounts.length > 0 && !selectedTestCount) {
      testCountToUse = availableCounts[0];
      setSelectedTestCount(testCountToUse);
    }
    // Fire plan change event
    const productKey = testCountToUse ? `core_${testCountToUse}_test` : null;
    const product = productKey ? products.find(p => p.key === productKey) || null : null;
    firePlanChange(planId, testCountToUse, paymentType, product);
  };

  const handleTestCountSelect = (count: number) => {
    setSelectedTestCount(count);
    // Fire plan change event for test count change
    const product = getSelectedProduct();
    firePlanChange(selectedPlan, count, paymentType, product);
  };

  const handleNextToDetails = () => {
    if (!selectedPlan || !selectedTestCount) return;
    
    const product = getSelectedProduct();
    let productId: string | null = null;
    
    if (product) {
      productId = paymentType === 'monthly' 
        ? product.stripe_price_monthly_id 
        : product.stripe_price_yearly_id;
    }

    // Store selected plan data
    const planData: SelectedPlanData = {
      type: selectedPlan,
      testCount: selectedTestCount,
      product: product,
      productId: productId,
      paymentType: paymentType,
    };
    localStorage.setItem('selectedPlan', JSON.stringify(planData));
    
    const token = searchParams.get('token');
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    navigate(`/signup/today${query}`);
  };

  return (
    <div className='c_container subscription-page-wrapper'>
      <div className='two-colum-grid' >
        <div className='left-column' >
          <div className='become-member-text' >Become a member</div>
          <div className='journey-starts-here' >Your journey starts here</div>
          <div className='choose-between-monthly-yearly' >Choose between monthly or yearly payment 
          <span className='tooltip-icon' >
              ⓘ
              <span className='tooltip-text' >
                <span className='tooltip-text-content' >You get all services up front. You can pay in 1 annual payment or in 12 monthly payments. Monthly payments commit you to the full 12-month term.</span>
              </span>
            </span>
          </div>
          <div className='d-flex align-items-center plan-term-wrapper' style={{ marginBottom: '16px' }}>
            <div className='plan-term-check' >
              <label>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'monthly'}
                  onChange={() => {
                    setPaymentType('monthly');
                    firePaymentTypeChange('monthly', selectedPlan, selectedTestCount);
                  }}
                />
                <span className='btn' >Monthly</span>
              </label>
              <label>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'yearly'}
                  onChange={() => {
                    setPaymentType('yearly');
                    firePaymentTypeChange('yearly', selectedPlan, selectedTestCount);
                  }}
                />
                <span className='btn' >Yearly</span>
              </label>
            </div>
            
          </div>
          <div className='' >
            {loading && products.length === 0 && (
              <div className='plans-loading-hint'>Loading latest prices…</div>
            )}
            <>
                <div
                  className={`plan-card-wrapper coreVitals-membership ${selectedPlan === 'coreVitals' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('coreVitals')}
                >
                  <PlanHeader
                    id='coreVitals'
                    title='CoreVitals Membership'
                    description='Ongoing health tracking without imaging - up to 4x blood tests/year'
                    price={getPlanPrice('coreVitals')}
                    selectedPlan={selectedPlan}
                    onSelect={(id) => handlePlanSelect(id as PlanType)}
                  />
                  {selectedPlan === 'coreVitals' && (
                    <BloodTestPerYear
                      selectedTestCount={selectedTestCount}
                      onTestCountChange={handleTestCountSelect}
                      availableTestCounts={getAvailableTestCounts()}
                    />
                  )}
                  {isMobile && selectedPlan === 'coreVitals' && (
                    <OrderSummary 
                      selectedPlanId={selectedPlan} 
                      paymentType={paymentType}
                      selectedTestCount={selectedTestCount}
                      getPlanPrice={getPlanPrice}
                    />
                  )}
                </div>
            </>

            <div>
              <button 
                disabled={!selectedPlan || !selectedTestCount} 
                onClick={handleNextToDetails} 
                className='plans-step-btn'
              >
                Next - your details <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className='right-column'>
            <div className='inner-card'>
            <h4 className='inner-card-title'>Order summary</h4>
            {selectedPlan ? (
              <OrderSummary 
                selectedPlanId={selectedPlan} 
                paymentType={paymentType}
                selectedTestCount={selectedTestCount}
                getPlanPrice={getPlanPrice}
              />
            ) : (
              <div className='inner-card-info'>
                <div className='select-plan-text'>Select a plan to get started.</div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansCore;
